import { upperCase } from 'lodash';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { axiosInstance } from '@config/axios/axios.config';

import { paths } from '@common/types';
import { stableKey } from '@common/cache/util.ts';

interface ITeamListProps {
  page: number;
  limit: number;
  sort_by?: string;
  sort_order?: string;
  search: string;
  state?: string;
  record_id?: string;
}

const teamsRequest = async (props: ITeamListProps) => {
  const response = axiosInstance.get<
    paths[`/record/teams`]['get']['responses']['200']['content']['application/json']
  >(`/record/teams`, {
    params: {
      page: props.page,
      limit: props.limit,
      ...(props.search !== '' && { search: props.search }),
      sort_by: props.sort_by,
      ...(props.sort_order && { sort_order: upperCase(props.sort_order) }),
      ...(props.state !== '' && { state: upperCase(props.state) }),
      ...(props.record_id !== '' && { record_id: props.record_id }),
    },
  });
  const data = (await response).data;
  return data;
};

export const useTeamsQuery = (props: ITeamListProps) => {
  const orgQuery = useQuery({
    queryKey: ['teams', stableKey(props)],
    queryFn: () => teamsRequest(props),
    placeholderData: keepPreviousData,
  });

  return orgQuery;
};

//Team names only.
const teamNamesRequest = async () => {
  const response =
    axiosInstance.get<
      paths[`/team/names`]['get']['responses']['200']['content']['application/json']
    >(`/team/names`);
  const data = (await response).data;
  return data;
};

export const useTeamNamesQuery = () => {
  const orgQuery = useQuery({
    queryKey: ['teamNames'],
    queryFn: () => teamNamesRequest(),
  });

  return orgQuery;
};

interface ITeamsShortProps {
  search?: string;
  record_id?: string;
}

//Both team names and ids
const teamsShortRequest = async (props: ITeamsShortProps) => {
  const response = axiosInstance.get<
    paths[`/record/teams/short`]['get']['responses']['200']['content']['application/json']
  >(`/record/teams/short`, { params: props });
  const data = (await response).data;
  return data;
};

export const useTeamsShortQuery = (props: ITeamsShortProps) => {
  const orgQuery = useQuery({
    queryKey: ['teamsShort', stableKey(props)],
    queryFn: () => teamsShortRequest(props),
  });

  return orgQuery;
};

interface ITeamEligibleUsersProps {
  teamId?: string;
  search?: string;
}

const teamEligibleUsersRequest = async (props: ITeamEligibleUsersProps) => {
  const response = axiosInstance.get<
    paths[`/team/{team_id}/eligible-users`]['get']['responses']['200']['content']['application/json']
  >(`/team/${props.teamId}/eligible-users`, {
    params: {
      search: props.search,
    },
  });
  const data = (await response).data;
  return data;
};

export const useTeamEligibleUsersQuery = (props: ITeamEligibleUsersProps) => {
  const orgQuery = useQuery({
    queryKey: ['teamEligibleUsers', props.teamId, props.search],
    queryFn: () => teamEligibleUsersRequest(props),
  });

  return orgQuery;
};

interface ITeamUsersListProps {
  teamId?: string;
  enabled?: boolean;
  refetchInterval?: number;
}

const teamRequest = async (props: ITeamUsersListProps) => {
  const response = axiosInstance.get<
    paths[`/team/{team_id}`]['get']['responses']['200']['content']['application/json']
  >(`/team/${props.teamId}`);
  const data = (await response).data;
  return data;
};

export const useTeamQuery = ({
  teamId,
  enabled = true,
  refetchInterval,
}: ITeamUsersListProps) => {
  const orgQuery = useQuery({
    queryKey: ['team', teamId],
    queryFn: () => teamRequest({ teamId }),
    enabled,
    refetchInterval: refetchInterval ?? undefined,
  });

  return orgQuery;
};

interface ITeamHistoryProps {
  page: number;
  limit: number;
  team_id: string;
  enabled: boolean;
}

const teamHistoryRequest = async (props: ITeamHistoryProps) => {
  const response = axiosInstance.get<
    paths[`/team/{team_id}/history`]['get']['responses']['200']['content']['application/json']
  >(`/team/${props.team_id}/history`, {
    params: {
      page: props.page,
      limit: props.limit,
    },
  });
  const data = (await response).data;

  return data;
};

export const useTeamHistoryQuery = (props: ITeamHistoryProps) => {
  const query = useQuery({
    enabled: !!props.enabled,
    queryKey: ['team', 'history', props.team_id, props.page],
    queryFn: () => teamHistoryRequest(props),
  });

  return query;
};
